import dayjs from "dayjs";
import {
  Card,
  Group,
  Heading,
  KpiGridSkeleton,
  ListaItemsSkeleton,
  PageHeader,
  RegionCargando,
  Skeleton,
  Stack,
  type ColumnaSkeleton,
} from "@/components/ui";
import { mesInicial } from "../utils/params-calendario";
import { SemaforoLeyenda } from "./SemaforoPago";

/** Título y subtítulo del calendario · compartidos por la página y su skeleton. */
export const ENCABEZADO_CALENDARIO_PAGOS = {
  titulo: "Calendario de pagos",
  descripcion: "Pagos programados de las solicitudes autorizadas · la fecha la fija Mesa de Control al autorizar el último nivel.",
} as const;

/** Columnas de `PagosTable` (vista Tabla) para su skeleton · mismo orden y alineación. */
export const COLUMNAS_PAGOS_SKELETON: ColumnaSkeleton[] = [
  { w: 90 },
  { w: 170, lineas: 2 },
  { w: 160 },
  { w: 140, lineas: 2 },
  { w: 110, align: "right" },
  { w: 110 },
  { w: 100 },
  { w: 100 },
  { w: 60, align: "center" },
  { w: 100 },
  { w: 90, align: "center" },
];

const SEMANAS = 5;

/**
 * Vista Calendario en carga · cuadrícula mensual 7×5 (mismas celdas de 92px que
 * `CalendarioMensual`) + panel del día a la derecha (mismo reparto 8/4).
 */
export function CalendarioMensualSkeleton() {
  return (
    <RegionCargando etiqueta="Cargando calendario…" className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-12">
      <div className="lg:col-span-8" aria-hidden="true">
        <Card padding="md">
          <div className="mb-1 grid grid-cols-7 gap-1">
            {Array.from({ length: 7 }, (_, i) => (
              <Skeleton key={i} height={10} width="40%" className="mx-auto" />
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: 7 * SEMANAS }, (_, i) => (
              <div key={i} className="flex min-h-[92px] flex-col gap-2 rounded-md border border-border p-1.5">
                <Skeleton height={10} width={16} />
                {i % 4 === 1 && <Skeleton height={16} width="80%" />}
              </div>
            ))}
          </div>
        </Card>
      </div>
      <div className="lg:col-span-4" aria-hidden="true">
        <Card padding="md" className="h-full">
          <Skeleton height={16} width="55%" />
          <ListaItemsSkeleton items={3} className="mt-3" />
        </Card>
      </div>
    </RegionCargando>
  );
}

/**
 * Calendario de pagos en su primera carga · MISMA forma que `CalendarioPagosPage`
 * en su vista por defecto (Calendario): encabezado real + refrescar, barra de mes
 * (con el mes en curso ya escrito) + sociedad/moneda/toggle, 4 KPIs, cuadrícula
 * mensual con el panel del día y la leyenda del semáforo (estática, real). Es el
 * `pendingComponent` de la ruta `/calendario-pagos` Y la rama `isPending` de la
 * página: una sola fuente, sin salto de forma al llegar los datos.
 */
export function CalendarioPagosPaginaSkeleton() {
  return (
    <RegionCargando etiqueta="Cargando calendario de pagos…" className="w-full px-4 py-6 md:px-6">
      <PageHeader
        title={ENCABEZADO_CALENDARIO_PAGOS.titulo}
        description={ENCABEZADO_CALENDARIO_PAGOS.descripcion}
        actions={<Skeleton height={36} width={36} aria-hidden="true" />}
      />
      <Stack gap="md">
        {/* Misma barra que la página: ‹ mes › a la izquierda · filtros y toggle a la derecha. */}
        <Group justify="space-between" className="flex-wrap" gap="sm" aria-hidden="true">
          <Group gap="xs">
            <Skeleton height={28} width={28} />
            <Heading order={4} className="min-w-42.5 text-center capitalize">
              {dayjs(mesInicial()).format("MMMM YYYY")}
            </Heading>
            <Skeleton height={28} width={28} />
          </Group>
          <Group gap="sm">
            <Skeleton height={36} className="w-58" />
            <Skeleton height={36} className="w-28" />
            <Skeleton height={30} width={140} />
          </Group>
        </Group>
        <div aria-hidden="true">
          <KpiGridSkeleton n={4} />
        </div>
        <div aria-hidden="true">
          <CalendarioMensualSkeleton />
        </div>
        <SemaforoLeyenda />
      </Stack>
    </RegionCargando>
  );
}
